import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../Icon";
import { QUIZ, scoreQuiz } from "../../data/tools";
import { getProduct } from "../../data/products";
import { useL } from "../../lib/useLang";
import { scrollToSection } from "../../lib/navigation";

export default function Recommender({ onOpenProduct }) {
  const { t } = useTranslation();
  const L = useL();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const current = QUIZ[step];
  const total = QUIZ.length;
  const progress = done ? 100 : Math.round((step / total) * 100);

  const choose = (optionId) => {
    setAnswers((prev) => ({ ...prev, [current.id]: optionId }));

    // Avance automático: una pregunta contestada no debería requerir
    // un clic extra para continuar.
    window.setTimeout(() => {
      if (step + 1 >= total) setDone(true);
      else setStep(step + 1);
    }, 220);
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setDone(false);
  };

  if (done) {
    const results = scoreQuiz(answers).slice(0, 3);

    return (
      <div>
        <div className="fsg-result__top">
          <div>
            <h3 style={{ marginBottom: ".35rem" }}>
              {t("recommender.resultTitle")}
            </h3>
            <p style={{ marginBottom: 0, fontSize: "var(--fsg-fs-sm)" }}>
              {t("recommender.resultLead")}
            </p>
          </div>
          <div className="fsg-result__score">
            <strong>{results[0]?.match ?? 0}%</strong>
            <span>{t("recommender.affinity")}</span>
          </div>
        </div>

        <ol className="fsg-result__list">
          {results.map((result, index) => {
            const product = getProduct(result.id);
            if (!product) return null;

            return (
              <li
                key={result.id}
                className={`fsg-result__item ${index === 0 ? "is-top" : ""}`}
              >
                <span className="fsg-result__rank" aria-hidden="true">
                  {index + 1}
                </span>
                <div>
                  {index === 0 && (
                    <span
                      className="fsg-badge"
                      style={{ marginBottom: ".5rem" }}
                    >
                      <Icon name="star" /> {t("recommender.topPick")}
                    </span>
                  )}
                  <h4>{L(product.name)}</h4>
                  <div className="fsg-match">
                    <div
                      className="fsg-progress"
                      role="img"
                      aria-label={`${result.match}% ${t(
                        "recommender.affinity"
                      )}`}
                    >
                      <div
                        className="fsg-progress__bar"
                        style={{ width: `${result.match}%` }}
                      />
                    </div>
                    <span>{result.match}%</span>
                  </div>
                  <p>{L(product.promise)}</p>
                  <div className="fsg-btn-row">
                    <button
                      type="button"
                      className="fsg-btn fsg-btn--ghost fsg-btn--sm"
                      onClick={() => onOpenProduct(product.id)}
                    >
                      {t("cta.viewDetail")}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="fsg-btn-row" style={{ marginTop: "var(--fsg-s-5)" }}>
          <button
            type="button"
            className="fsg-btn fsg-btn--primary"
            onClick={() => scrollToSection("contacto")}
          >
            {t("recommender.talkAboutIt")}
            <Icon name="arrow" />
          </button>
          <button
            type="button"
            className="fsg-btn fsg-btn--ghost"
            onClick={restart}
          >
            {t("recommender.restart")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="fsg-quiz__progress">
        <div
          className="fsg-progress"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t("recommender.title")}
        >
          <div className="fsg-progress__bar" style={{ width: `${progress}%` }} />
        </div>
        <span>
          {t("recommender.step", { current: step + 1, total })}
        </span>
      </div>

      <h3 className="fsg-quiz__question">{L(current.question)}</h3>
      {current.help && <p className="fsg-quiz__help">{L(current.help)}</p>}

      <div className="fsg-options" role="group" aria-label={L(current.question)}>
        {current.options.map((option) => (
          <button
            key={option.id}
            type="button"
            className="fsg-option"
            aria-pressed={answers[current.id] === option.id}
            onClick={() => choose(option.id)}
          >
            <span className="fsg-option__mark" aria-hidden="true">
              <Icon name="check" />
            </span>
            <span>
              <strong>{L(option.label)}</strong>
              {option.desc && <span className="desc">{L(option.desc)}</span>}
            </span>
          </button>
        ))}
      </div>

      {step > 0 && (
        <div className="fsg-btn-row" style={{ marginTop: "var(--fsg-s-5)" }}>
          <button
            type="button"
            className="fsg-btn fsg-btn--ghost fsg-btn--sm"
            onClick={() => setStep(step - 1)}
          >
            {t("recommender.back")}
          </button>
        </div>
      )}
    </div>
  );
}
